// apps/client/src/services/record.ts

import {
  Record,
  NewRecord,
  RecordListResponse,
  RecordCreateResponse,
  RecordGetResponse,
  RecordDeleteResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

export const fetchRecords = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  options?: {
    startDate?: Date;
    endDate?: Date;
    submittedBy?: string;
    limit?: number;
    offset?: number;
  },
  signal?: AbortSignal
): Promise<{ records: Record[]; total: number }> => {
  let url = `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/entries`;

  const params = new URLSearchParams();
  if (options?.startDate) {
    params.append('startDate', options.startDate.toISOString());
  }
  if (options?.endDate) {
    params.append('endDate', options.endDate.toISOString());
  }
  if (options?.submittedBy) {
    params.append('submittedBy', options.submittedBy);
  }
  if (options?.limit) {
    params.append('limit', options.limit.toString());
  }
  if (options?.offset) {
    params.append('offset', options.offset.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await apiClient.get(url, signal);

  if (!response.ok) {
    throw new Error(`Failed to fetch records: ${response.statusText}`);
  }

  const result: RecordListResponse = await response.json();
  return {
    records: result.records || [],
    total: result.total || 0,
  };
};

export const fetchRecord = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  recordId: string,
  signal?: AbortSignal
): Promise<Record> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/entries/${recordId}`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch record: ${response.statusText}`);
  }

  const result: RecordGetResponse = await response.json();
  return result.record;
};

export const createRecord = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  data: NewRecord,
  signal?: AbortSignal
): Promise<Record> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/entries`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create record: ${response.statusText}`);
  }

  const result: RecordCreateResponse = await response.json();
  return result.record;
};

export const createRecordByCollectionId = async (
  collectionId: string,
  data: NewRecord,
  signal?: AbortSignal
): Promise<Record> => {
  const response = await apiClient.post(
    `/collections/${collectionId}/entries`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create record: ${response.statusText}`);
  }

  const result: RecordCreateResponse = await response.json();
  return result.record;
};

export const deleteRecord = async (
  organisationId: string,
  planId: string,
  collectionId: string,
  recordId: string,
  signal?: AbortSignal
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/records/${collectionId}/entries/${recordId}`,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete record: ${response.statusText}`);
  }
};
